import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, withCtx as _withCtx, createSlots as _createSlots, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = {
  id: "rooms",
  class: "main-content-wrapper"
}
const _hoisted_2 = /*#__PURE__*/_createElementVNode("h1", { class: "main-content-page-title" }, "部屋・設備登録", -1)
const _hoisted_3 = { class: "live-message-templates-body" }
const _hoisted_4 = { class: "w-full text-right pr-4" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = ["onClick"]
const _hoisted_7 = { class: "grid" }
const _hoisted_8 = { class: "col-12" }
const _hoisted_9 = /*#__PURE__*/_createElementVNode("label", { class: "font-normal mb-2 block" }, [
  /*#__PURE__*/_createTextVNode("名前 "),
  /*#__PURE__*/_createElementVNode("span", { class: "pl-1 text-red" }, "必須")
], -1)
const _hoisted_10 = { class: "col-12" }
const _hoisted_11 = /*#__PURE__*/_createElementVNode("label", { class: "font-normal mb-2 block" }, "容量", -1)
const _hoisted_12 = { class: "col-12 flex justify-content-center mt-2" }

import { onBeforeMount, ref, inject, computed } from "vue";
import { useStore } from "vuex";
import { get, set } from "@vueuse/core";
import { PageState } from "primevue/paginator";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import moment from "moment";
import { DEFAULT_PAGINATOR_ROWS } from "@/clinic/typings/constants/paginator";
import Header from "@/clinic/components/atoms/Grid/header.vue";
import Grid from "@/clinic/components/atoms/Grid/index.vue";
import Paginator from "@/clinic/components/atoms/Paginator/index.vue";
import { RoomServices } from "@/clinic/services";
import type { ApiBasePaginatorReq, ApiRoomIndexReq, ApiRoomReq } from "@/clinic/typings";
import { notityErrorMsg, notifySuccessMsg } from "@/clinic/utils/notify-utils";
import { ClinicPartner } from "@/clinic/typings/enums/clinic";


export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const confirm = useConfirm();
const toast = useToast();
const store = useStore();

const clinicId = ref(0);
const loading = ref(true);
const rooms = ref([]);
const selectedRoom = ref<Record<string, unknown>>({});
const formVisible = ref(false);
const formHeader = ref("");
const columns = ref([
  { field: "name", header: "名前", class: "col-4" },
  { field: "capacity", header: "容量", class: "col-2" },
  { field: "menus_count", header: "メニュー数", class: "col-2" },
  { field: "created_at", header: "作成日", class: "col-2" },
  { field: "actions", header: "", class: "col-2" },
]);
const searchTerm = ref("");
const rows = ref(DEFAULT_PAGINATOR_ROWS);
const totalCount = ref(0);
const page = ref(0);
const first = ref(0);
const pgTop = ref<InstanceType<typeof Paginator> | null>(null);
const pgBot = ref<InstanceType<typeof Paginator> | null>(null);

const paginatorParams = computed(
  (): ApiBasePaginatorReq =>
    ({
      term: get(searchTerm),
      page: get(page) + 1, // バックエンドは1ベースのページ番号を期待
      rows: get(rows),
    } as ApiBasePaginatorReq)
);

const isUnaffiliated = () => {
  return store.getters["authClient/selectedClinic"].partner == ClinicPartner.Unaffiliated;
};

const fetchRooms = async () => {
  set(loading, true);
  const { data } = await RoomServices.fetchRooms(
    { ...{ clinic_id: get(await get(clinicId)) }, ...get(paginatorParams) } as ApiRoomIndexReq,
    () => undefined,
    () => undefined,
    () => set(loading, false)
  );
  set(rooms, data.value.rooms);
  set(totalCount, data.value.meta.total_count);
};

const paging = async (fromId: string, { page: newPage, first: newFirst }: PageState) => {
  // 現在のページと同じ場合は何もしない
  if (newPage === get(page)) return;

  // ページ番号を更新
  set(page, newPage);

  // firstの値を更新（PrimeVueから渡された値を使用）
  set(first, newFirst);

  await fetchRooms();
};

const onSearch = async (event: Event) => {
  const target = event.currentTarget as HTMLInputElement;
  set(searchTerm, target?.value || "");
  // 検索時はページをリセット
  set(page, 0);
  set(first, 0);
  await fetchRooms();
};

const addRoom = () => {
  set(formVisible, true);
  set(formHeader, "部屋を作る");
};

const editRoom = (event: Event, roomId: number) => {
  event.stopPropagation();
  const room = get(rooms).find((room: Record<string, unknown>) => room.id == roomId);
  set(selectedRoom, room ? { ...(room as Record<string, unknown>) } : {});
  set(formVisible, true);
  set(formHeader, "編集室");
};

const handleSaveRoom = async () => {
  await RoomServices.saveRoom(
    { ...{ clinic_id: get(await get(clinicId)) }, ...get(selectedRoom) },
    async () => {
      notifySuccessMsg(toast, "正常に保存");
      await fetchRooms();
      closePopupForm();
    },
    (errors: string[]) => notityErrorMsg(toast, errors.join("<br />"))
  );
};

const deleteRoom = async (event: Event, roomId: number) => {
  event.stopPropagation();

  confirm.require({
    message: "この部屋を削除しますか？",
    header: "部屋を削除",
    rejectLabel: "キャンセル",
    acceptLabel: "削除",
    acceptClass: "p-button-danger",
    accept: async () => {
      await RoomServices.deleteRoom(
        { ...{ clinic_id: get(await get(clinicId)) }, ...{ id: roomId } } as ApiRoomReq,
        async () => {
          notifySuccessMsg(toast, "正常に削除されました");
          await fetchRooms();
        },
        () => undefined
      );
    },
  });
};

const closePopupForm = () => {
  set(selectedRoom, {});
  set(formVisible, false);
};

onBeforeMount(async () => {
  set(clinicId, await inject("clinicId"));
  fetchRooms();
});

return (_ctx: any,_cache: any) => {
  const _component_InputText = _resolveComponent("InputText")!
  const _component_Button = _resolveComponent("Button")!
  const _component_Dialog = _resolveComponent("Dialog")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _hoisted_2,
    _createVNode(Header, {
      enableSearch: "",
      onSearch: onSearch,
      onNew: addRoom,
      createButtonLabel: "作成",
      searchPlaceholder: "名前を入力",
      enableCreate: isUnaffiliated()
    }, null, 8, ["enableCreate"]),
    _createElementVNode("div", _hoisted_3, [
      (totalCount.value > rows.value)
        ? (_openBlock(), _createBlock(Paginator, {
            key: 0,
            id: "pg-top",
            ref_key: "pgTop",
            ref: pgTop,
            modelValue: first.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((first).value = $event)),
            onPaging: paging,
            totalRecords: totalCount.value,
            rows: rows.value,
            paginatorId: "pg-top"
          }, null, 8, ["modelValue", "totalRecords", "rows"]))
        : _createCommentVNode("", true),
      _createVNode(Grid, {
        items: rooms.value,
        columns: columns.value,
        loading: loading.value
      }, _createSlots({
        created_at: _withCtx(({ data }) => [
          _createElementVNode("span", null, _toDisplayString(_unref(moment)(data.created_at).format("YYYY/MM/DD")), 1)
        ]),
        menus_count: _withCtx(({ data }) => [
          _createElementVNode("span", null, _toDisplayString(data.menus_count), 1)
        ]),
        _: 2
      }, [
        (isUnaffiliated())
          ? {
              name: "actions",
              fn: _withCtx(({ data }) => [
                _createElementVNode("div", _hoisted_4, [
                  _createElementVNode("span", {
                    class: "pi pi-pencil text-green-600 mr-4",
                    onClick: ($event: any) => (editRoom($event, data.id))
                  }, null, 8, _hoisted_5),
                  (data.menus_count == 0)
                    ? (_openBlock(), _createElementBlock("span", {
                        key: 0,
                        class: "pi pi-trash text-red-600",
                        onClick: ($event: any) => (deleteRoom($event, data.id))
                      }, null, 8, _hoisted_6))
                    : _createCommentVNode("", true)
                ])
              ]),
              key: "0"
            }
          : undefined
      ]), 1032, ["items", "columns", "loading"]),
      (totalCount.value > rows.value)
        ? (_openBlock(), _createBlock(Paginator, {
            key: 1,
            id: "pg-bot",
            ref_key: "pgBot",
            ref: pgBot,
            modelValue: first.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((first).value = $event)),
            onPaging: paging,
            totalRecords: totalCount.value,
            rows: rows.value,
            class: "mt-2",
            paginatorId: "pg-bot"
          }, null, 8, ["modelValue", "totalRecords", "rows"]))
        : _createCommentVNode("", true)
    ]),
    _createVNode(_component_Dialog, {
      visible: formVisible.value,
      "onUpdate:visible": _cache[4] || (_cache[4] = ($event: any) => ((formVisible).value = $event)),
      modal: "",
      header: formHeader.value,
      style: { width: '30vw' }
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("div", _hoisted_8, [
            _hoisted_9,
            _createVNode(_component_InputText, {
              type: "text",
              modelValue: selectedRoom.value.name,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectedRoom.value.name) = $event)),
              placeholder: "部屋・設備名を入力してください"
            }, null, 8, ["modelValue"])
          ]),
          _createElementVNode("div", _hoisted_10, [
            _hoisted_11,
            _createVNode(_component_InputText, {
              type: "number",
              modelValue: selectedRoom.value.capacity,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((selectedRoom.value.capacity) = $event)),
              min: "1"
            }, null, 8, ["modelValue"])
          ]),
          _createElementVNode("div", _hoisted_12, [
            _createVNode(_component_Button, {
              label: "キャンセル",
              class: "p-button-light mr-5",
              onClick: closePopupForm
            }),
            _createVNode(_component_Button, {
              label: "保存",
              class: "btn-orange text-white",
              disabled: !selectedRoom.value.name,
              onClick: handleSaveRoom
            }, null, 8, ["disabled"])
          ])
        ])
      ]),
      _: 1
    }, 8, ["visible", "header"])
  ]))
}
}

})